<template>
  <b-card
    class="mb-3 p-0"
    :style="`height: 100%; ${
      disabled ? 'opacity: 0.5; cursor: not-allowed' : 'cursor: pointer;'
    }`"
    :class="`${isSelected ? 'shadow' : ''}`"
    :border-variant="borderVariant"
    @click.stop="!disabled && $emit('select-product')"
  >
    <b-card-text class="fs-14" :title="titleInfo">
      <div v-if="!hideCheckbox">
        <b-checkbox
          :checked="isSelected"
          @change="$emit('select')"
        ></b-checkbox>
      </div>
      <div class="text-center mb-2" v-if="product.photo">
        <b-img
          fluid
          :src="getImageLinkByOrigin(product.origem) + product.photo"
          style="min-height: 50px; max-height: 50px"
        >
        </b-img>
      </div>
      <div class="mb-2" :title="product.product || product.produto">
        <span
          style="
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            min-height: 40px;
            max-height: 40px;
          "
        >
          {{ product.product || product.produto }}
        </span>
      </div>
      <div class="d-flex align-items-center mb-2" @click.stop>
        <i class="far fa-money-bill-alt mr-2"></i>
        <span v-if="!editingPrice">
          {{ formatNumber(product.priceSalesFormat1 || product.price) }}
        </span>
        <InputMoney
          v-else
          v-model="fields.priceSalesFormat1"
          style="height: 25px; width: 55%; font-size: 14px"
          @keydown.enter="updateLocalPrice"
        />
        <i
          v-if="!editingPrice"
          class="far fa-edit ml-2 text-primary"
          title="Editar preço"
          @click.stop="startEditing"
        ></i>
        <div v-else>
          <i
            class="far fa-check-circle ml-1 text-success"
            title="Salvar valor"
            v-if="!loading"
            @click.stop="updateLocalPrice"
          ></i>
          <i
            class="far fa-trash-alt ml-2 text-danger"
            title="Cancelar edição"
            v-if="!loading"
            @click.stop="cancelEdit"
          ></i>
          <b-spinner v-if="loading" small class="ml-2"></b-spinner>
        </div>
      </div>
      <div :class="product.quantityStock <= 0 ? 'text-danger' : ''">
        <i class="fas fa-box mr-2"></i>
        Em estoque: {{ product.quantityStock }}
      </div>
      <div class="mb-2" @click.stop>
        <div v-if="loadingCategories">
          <b-spinner small class="mr-2" /> Carregando categorias do Mercado
          Livre
        </div>
        <div class="d-flex align-items-center" v-else>
          <i class="fas fa-tag mr-2"></i>
          <SelectItems
            v-model="fields.categoryId"
            label="category_name"
            placeholder="Selecione uma categoria"
            :reduceFunction="(category) => category.category_id"
            :items="categories"
            @update:value="$event && $emit('update-category', $event)"
          />
        </div>
      </div>
      <div @click.stop>
        <div v-if="loadingAttributes">
          <b-spinner small class="mr-2" /> Carregando atributos da categoria
        </div>
        <div v-else-if="attributes.groups.length && !this.attributesHasInputs">
          <span class="text-success">
            <i class="fas fa-check-double mr-1"></i> Sem atributos obrigatórios
            para essa categoria
          </span>
        </div>
        <div v-else-if="attributes.groups.length && this.attributesHasInputs">
          <div
            v-for="group in groupsWithInputsToShow"
            :key="group.id"
            class="mt-2"
          >
            <div class="font-weight-bold">{{ group.label }}</div>
            <div
              v-for="(component, index) in group.components"
              :key="`${index}-${component.label}`"
              class="d-flex flex-column"
            >
              <div
                v-for="attribute in component.attributes.filter((x) =>
                  hasInputsToShow(x)
                )"
                :key="attribute.id"
                class="mt-2"
              >
                <span>{{ attribute.name }}:</span
                ><span class="text-danger">*</span>

                <SelectItems
                  v-if="attribute.value_type === 'list'"
                  v-model="fields.internalAttributes[attribute.id]"
                  label="name"
                  placeholder="Selecione uma opção"
                  :reduceFunction="(option) => option.id"
                  :items="attribute.values"
                  @update:value="updateAttributes()"
                />
                <b-input
                  v-else
                  size="sm"
                  v-model="fields.internalAttributes[attribute.id]"
                  @input="updateAttributesDebounce()"
                />
                <b-form-text
                  v-if="(component.ui_config || {}).hint"
                  id="input-live-help"
                >
                  {{ component.ui_config.hint }}
                </b-form-text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import SelectItems from 'Components/Inputs/SelectItems.vue';
import { convertBrlToDecimal, formatNumber, debounce } from '@/utils';
import { getImageLinkByOrigin } from '@/utils/photos.js';
import InputMoney from '@/components/Inputs/InputMoney.vue';
import shopkeeperAppApi from '@/api/shopkeeperAppApi';

export default {
  name: 'MercadoLivreProductCard',
  emits: ['update-price', 'select', 'update-category', 'update-attributes'],
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideCheckbox: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['dadosUsuario']),
    borderVariant() {
      if (this.isSelected) return 'primary';
      return '';
    },
    titleInfo() {
      if (this.disabled)
        return 'Produto sem estoque não pode ser integrado ao Mercado Livre';
      return '';
    },
    groupsWithInputsToShow() {
      return this.attributes.groups.filter((group) =>
        group.components.some((comp) =>
          comp.attributes.some((x) => this.hasInputsToShow(x))
        )
      );
    },
    /**
     * O atributo BRAND será preenchido automaticamente no back
     */
    attributesHasInputs() {
      return this.attributes.groups
        .flatMap((group) => group.components)
        .some((comp) => comp.attributes.some((x) => this.hasInputsToShow(x)));
    },
  },
  data() {
    return {
      loading: false,
      loadingCategories: false,
      loadingAttributes: false,
      editingPrice: false,
      fields: {
        priceSalesFormat1: '',
        categoryId: '',
        internalAttributes: {},
      },
      categories: [],
      attributes: {
        groups: [],
      },
      timer: null,
    };
  },
  watch: {
    isSelected() {
      if (!this.isSelected || this.disabled || this.categories.length > 0)
        return;

      this.fetchPredictCategory();
    },
    'fields.categoryId': function () {
      this.$set(this.product, 'categoryId', this.fields.categoryId);
      this.$set(this.product, 'attributes', []);
      this.fields.internalAttributes = {};

      if (this.product.codbar) {
        this.$set(this.fields.internalAttributes, 'GTIN', this.product.codbar);
      }

      this.attributes.groups = [];
      this.updateAttributes();

      this.fetchCategoryAttributes();
    },
  },
  mounted() {
    this.fields.priceSalesFormat1 =
    this.product.priceSalesFormat1 || this.product.price || '';
    this.fields.categoryId = this.product.categoryId || '';

    this.fetchPredictCategory();
  },
  methods: {
    ...mapActions('shopkeeper', ['updateSelectedProduct']),
    formatNumber,
    getImageLinkByOrigin,
    startEditing() {
      this.editingPrice = true;
      this.fields.priceSalesFormat1 =
        this.product.priceSalesFormat1 || this.product.price;
    },
    cancelEdit() {
      this.editingPrice = false;
      this.fields.priceSalesFormat1 = '';
    },
    updateLocalPrice() {
      this.$emit(
        'update-price',
        convertBrlToDecimal(this.fields.priceSalesFormat1).toFixed(2)
      );

      this.$set(
        this.product,
        'priceSalesFormat1',
        this.fields.priceSalesFormat1
      );

      this.editingPrice = false;
      this.fields.priceSalesFormat1 = '';
    },
    updateAttributesDebounce() {
      if (this.timer) clearTimeout(this.timer);

      this.timer = setTimeout(() => {
        this.updateAttributes();
      }, 800);
    },
    updateAttributes() {
      const temp = [];

      Object.keys(this.fields.internalAttributes).forEach((key) => {
        temp.push({
          id: key,
          value_name: this.fields.internalAttributes[key],
        });
      });

      this.$set(this.product, 'attributes', temp);

      this.$emit('update-attributes', temp);
    },
    async fetchPredictCategory() {
      try {
        if (this.disabled || !this.isSelected) return;

        this.loadingCategories = true;

        const { data } = await shopkeeperAppApi.post(
          `/integration/marketplace/mercado-livre/predict-category`,
          {
            productId: this.product.productId,
            construpUserId: this.dadosUsuario.id,
          }
        );

        this.categories = data.data;
      } finally {
        this.loadingCategories = false;
      }
    },
    async fetchCategoryAttributes() {
      try {
        if (!this.fields.categoryId || this.disabled || !this.isSelected)
          return;

        this.loadingAttributes = true;

        this.attributes = {
          groups: [],
        };

        const { data } = await shopkeeperAppApi.post(
          `/integration/marketplace/mercado-livre/category-attributes`,
          {
            construpUserId: this.dadosUsuario.id,
            categoryId: this.fields.categoryId,
          }
        );

        this.attributes = data.data || {
          groups: [],
        };
      } finally {
        this.loadingAttributes = false;
      }
    },
    hasInputsToShow(attribute) {
      return (
        attribute.id !== 'BRAND' &&
        (attribute.tags.includes('required') ||
          attribute.tags.includes('conditional_required') ||
          attribute.tags.includes('new_required'))
      );
    },
  },
  components: {
    InputMoney,
    SelectItems,
  },
};
</script>
